import React from "react";
import { GoogleMap, LoadScript, Marker, Polyline } from "@react-google-maps/api";
import { Tabs } from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  ContainerOutlined,
  EnvironmentOutlined,
  InboxOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";

const { TabPane } = Tabs;

const routeTimeline = [
  {
    location: "Genova, IT",
    terminal: "Terminal Name",
    state: "done",
    events: [
      {
        label: "Empty Container Release to Shipper",
        date: "Mon, 17 Nov, 08:30 AM",
        badge: "Arrival Date",
        badgeClass: "arrival",
      },
    ],
  },
  {
    location: "Genova, IT",
    terminal: "Terminal Name",
    state: "done",
    highlight: true,
    events: [
      {
        label: "Gate In to Outbound Terminal",
        date: "Mon, 17 Nov, 10:30 AM",
        badge: "Arrival Date",
        badgeClass: "arrival",
      },
      {
        label: "Loaded on Vessel at Port of Loading",
        date: "Mon, 17 Nov, 10:30 AM",
        badge: "Estimated Date",
        badgeClass: "estimated",
      },
      {
        label: "Vessel Departure from Port of Loading",
        date: "Mon, 17 Nov, 10:30 AM",
        badge: "Early Arrival",
        badgeClass: "early",
      },
    ],
  },
  {
    location: "Cape Town, SA",
    terminal: "Terminal Name",
    state: "current",
    events: [
      {
        label: "Vessel Arrival at Port of Discharge",
        date: "Tue, 05 Dec, 09:00 AM",
        badge: "Estimated Date",
        badgeClass: "estimated",
      },
    ],
  },
  {
    location: "Cape Town, SA",
    terminal: "Terminal Name",
    state: "current",
    events: [
      {
        label: "Unloaded from Vessel at Port of Discharging",
        date: "Tue, 05 Dec, 09:00 AM",
        badge: "Estimated Date",
        badgeClass: "estimated",
      },
      {
        label: "Gate Out from Inbound Terminal",
        date: "Tue, 05 Dec, 09:00 AM",
        badge: "Delay",
        badgeClass: "delay",
      },
    ],
  },
  {
    location: "Jawaharlal Nehru, IN",
    terminal: "",
    state: "future",
    events: [
      {
        label: "Gate Out from Inbound Terminal",
        date: "Tue, 24 Dec, 03:20 PM",
        badge: "Estimated Date",
        badgeClass: "estimated",
      },
    ],
  },
];

const infoItems = [
  { label: "Status", value: "In Transit", valueClass: "pill" },
  {
    label: "Estimated Time of Arrival",
    value: "Wed, 24 Dec 2025",
    icon: <CalendarOutlined />,
  },
  {
    label: "Current Location",
    value: "Cape Town, SA",
    icon: <EnvironmentOutlined />,
  },
  {
    label: "Last Updated",
    value: "Wed, 24 Dec 2025",
    icon: <ClockCircleOutlined />,
  },
];

const googleMapApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const mapCenter = { lat: 7.5, lng: 38.5 };
const mapOptions = {
  mapTypeControl: false,
  streetViewControl: false,
  clickableIcons: false,
  fullscreenControl: true,
  zoomControl: true,
  mapTypeId: "roadmap",
};

const routePoints = {
  origin: { lat: 44.4056, lng: 8.9463 },
  current: { lat: -33.9249, lng: 18.4241 },
  destination: { lat: 18.949, lng: 72.951 },
};

const completedSeaRoute = [
  routePoints.origin,
  { lat: 42.6, lng: 6.2 },
  { lat: 39.4, lng: 2.2 },
  { lat: 36.1, lng: -5.4 },
  { lat: 29.4, lng: -12.5 },
  { lat: 19.8, lng: -18.8 },
  { lat: 7.2, lng: -20.8 },
  { lat: -5.4, lng: -16.3 },
  { lat: -17.8, lng: -6.5 },
  { lat: -28.7, lng: 7.5 },
  routePoints.current,
];

const pendingSeaRoute = [
  routePoints.current,
  { lat: -36.4, lng: 28.5 },
  { lat: -35.2, lng: 42.8 },
  { lat: -29.8, lng: 55.8 },
  { lat: -17.2, lng: 66.4 },
  { lat: -2.8, lng: 70.8 },
  { lat: 9.6, lng: 70.2 },
  routePoints.destination,
];

const createMapPin = (color) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg width="30" height="40" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 39C15 39 27 25.86 27 15C27 7.82 21.63 2 15 2C8.37 2 3 7.82 3 15C3 25.86 15 39 15 39Z" fill="${color}" stroke="white" stroke-width="3"/>
      <circle cx="15" cy="15" r="5.5" fill="white"/>
    </svg>
  `)}`;

const markerIcons = {
  origin: createMapPin("#202328"),
  current: createMapPin("#2db2e3"),
  destination: createMapPin("#202328"),
};

const completeRouteOptions = {
  strokeColor: "#28a8de",
  strokeOpacity: 1,
  strokeWeight: 3,
  geodesic: true,
};

const pendingRouteOptions = {
  strokeColor: "#596f7c",
  strokeOpacity: 0.75,
  strokeWeight: 2,
  geodesic: true,
};

const MapViewTab = () => {
  const [dashSymbol, setDashSymbol] = React.useState(null);
  const pendingOptions = dashSymbol
    ? {
        ...pendingRouteOptions,
        strokeOpacity: 0,
        icons: [
          {
            icon: dashSymbol,
            offset: "0",
            repeat: "12px",
          },
        ],
      }
    : pendingRouteOptions;

  const handleMapLoad = () => {
    if (window.google?.maps) {
      setDashSymbol({
        path: "M 0,-1 0,1",
        strokeOpacity: 1,
        strokeColor: "#596f7c",
        scale: 2,
      });
    }
  };

  const routeTimelineContent = (
    <>
      <div className="shipment-timeline-heading">
        <h3>Shipment Timeline</h3>
        <p>Track the journey of your shipment from origin to destination</p>
      </div>

      <div className="shipment-route-list">
        {routeTimeline.map((stop) => (
          <div
            className={`shipment-route-row ${stop.state}`}
            key={`${stop.location}-${stop.events[0].label}`}
          >
            <div className="route-stop">
              <span />
              <strong className={stop.highlight ? "highlight" : ""}>
                {stop.location}
              </strong>
              {stop.terminal && <p>{stop.terminal}</p>}
            </div>
            <div className="route-events">
              {stop.events.map((event) => (
                <div className="route-event" key={event.label}>
                  <span className={event.label.includes("Gate In") ? "linkish" : ""}>
                    {event.label}
                  </span>
                  <time>{event.date}</time>
                  <b className={`status-badge ${event.badgeClass}`}>
                    {event.badge}
                  </b>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="shipment-map-view">
      <div className="shipment-map-top">
        <section className="shipment-summary-panel">
          <div className="shipment-summary-head">
            <div className="shipment-brand-mark">ONE</div>
            <div className="shipment-title-block">
              <h2>CT - TCNU4493940</h2>
              <p>
                <InboxOutlined /> LTL Shipment <span>3 packages</span>
              </p>
            </div>
            <div className="shipment-summary-actions">
              <span className="container-chip">
                <ContainerOutlined /> 1 x 40' High Cube Dry
              </span>
              <button className="share-chip" type="button">
                <ShareAltOutlined /> Share
              </button>
            </div>
          </div>

          <div className="shipment-info-grid">
            {infoItems.map((item) => (
              <div className="shipment-info-item" key={item.label}>
                <span>{item.label}</span>
                <strong className={item.valueClass || ""}>
                  {item.icon}
                  {item.value}
                </strong>
              </div>
            ))}
          </div>

          <div className="shipment-progress-head">
            <strong>Shipment Progress</strong>
            <span>60% Complete</span>
          </div>
          <div className="shipment-progress-track">
            <div className="shipment-progress-fill">
              <span className="ship-position">ship</span>
            </div>
          </div>
          <div className="shipment-progress-labels">
            <span>
              <InboxOutlined /> Shipped, 17 Nov
            </span>
            <span className="active">
              <ContainerOutlined /> In Transit
            </span>
            <span>
              <ClockCircleOutlined /> Estimated Delivery, 24 Dec
            </span>
          </div>

          <div className="shipment-location-cards">
            <div>
              <label>From</label>
              <span>Origin</span>
              <article>
                <strong>Genova, IT</strong>
                <p>Shipped on Monday, 17 Nov 2025</p>
              </article>
            </div>
            <div>
              <label>To</label>
              <span>Destination</span>
              <article>
                <strong>Jawaharlal Nehru, IN</strong>
                <p>Expected by Wednesday, 24 Nov 2025</p>
              </article>
            </div>
          </div>
        </section>

        <section className="shipment-map-panel" aria-label="Shipment route map">
          {googleMapApiKey ? (
            <LoadScript googleMapsApiKey={googleMapApiKey}>
              <GoogleMap
                mapContainerClassName="shipment-google-map"
                center={mapCenter}
                zoom={2}
                options={mapOptions}
                onLoad={handleMapLoad}
              >
                <Polyline
                  path={completedSeaRoute}
                  options={completeRouteOptions}
                />
                <Polyline
                  path={pendingSeaRoute}
                  options={pendingOptions}
                />
                <Marker
                  position={routePoints.origin}
                  title="Genova, IT"
                  icon={markerIcons.origin}
                />
                <Marker
                  position={routePoints.current}
                  title="Cape Town, SA"
                  icon={markerIcons.current}
                />
                <Marker
                  position={routePoints.destination}
                  title="Jawaharlal Nehru, IN"
                  icon={markerIcons.destination}
                />
              </GoogleMap>
            </LoadScript>
          ) : (
            <div className="shipment-map-missing-key">
              Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY
              in the environment file.
            </div>
          )}
        </section>
      </div>

      <section className="shipment-timeline-panel">
        <Tabs className="shipment-common-tabs" defaultActiveKey="route" type="card">
          <TabPane tab="Route" key="route">
            {routeTimelineContent}
          </TabPane>
          <TabPane tab="Vessel" key="vessel">
            {routeTimelineContent}
          </TabPane>
          <TabPane tab="Sailing Information" key="sailing">
            {routeTimelineContent}
          </TabPane>
        </Tabs>
      </section>
    </div>
  );
};

export default MapViewTab;
